import { parseServerMessage, type RealtimeEnvelope } from "@/shared/realtime/envelope";

export type RealtimeStatus = "idle" | "connecting" | "live" | "offline";

export interface Ticket {
  ticket: string;
  path: string;
}

/** Everything the connection touches outside itself, so tests can drive it by hand. */
export interface ConnectionDeps {
  fetchTicket: () => Promise<Ticket>;
  openSocket: (url: string) => WebSocket;
  socketUrl: (path: string, ticket: string) => string;
  onEvent: (envelope: RealtimeEnvelope) => void;
  /** Messages may have been missed (a reconnect, a server restart): refetch everything. */
  onResync: () => void;
  onStatus: (status: RealtimeStatus) => void;
  random?: () => number;
}

export const BACKOFF_FIRST_MS = 1_000;
export const BACKOFF_MAX_MS = 30_000;
/* The server pings every 25 s; silence for longer than this means the line is dead. */
export const SILENCE_LIMIT_MS = 70_000;
const WATCHDOG_MS = 10_000;
/* A tab hidden this long lets its socket go; it reconnects (and catches up) when shown. */
export const HIDDEN_PAUSE_MS = 5 * 60_000;
const CLOSE_NORMAL = 1000;

/** Exponential backoff with jitter: about 1 s, 2 s, 4 s... never more than 30 s. */
export function reconnectDelay(attempt: number, random: () => number = Math.random): number {
  const ceiling = Math.min(BACKOFF_MAX_MS, BACKOFF_FIRST_MS * 2 ** Math.max(0, attempt));
  return ceiling / 2 + random() * (ceiling / 2);
}

/** The socket address on this origin: wss behind https, ws in development. */
export function socketUrlFor(location: Pick<Location, "protocol" | "host">) {
  return (path: string, ticket: string): string => {
    const scheme = location.protocol === "https:" ? "wss:" : "ws:";
    return `${scheme}//${location.host}${path}?ticket=${encodeURIComponent(ticket)}`;
  };
}

/**
 * One person's live connection: ticket, socket, heartbeat, reconnects. Framework-free; the
 * provider starts it for the signed-in person and stops it when they leave.
 */
export class RealtimeConnection {
  private socket: WebSocket | null = null;
  private attempt = 0;
  private everReady = false;
  private stopped = true;
  private paused = false;
  private lastHeard = 0;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private pauseTimer: ReturnType<typeof setTimeout> | undefined;
  private watchdog: ReturnType<typeof setInterval> | undefined;

  constructor(private readonly deps: ConnectionDeps) {}

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.watchdog = setInterval(() => {
      this.checkSilence();
    }, WATCHDOG_MS);
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    clearTimeout(this.retryTimer);
    clearTimeout(this.pauseTimer);
    clearInterval(this.watchdog);
    this.drop();
    this.deps.onStatus("idle");
  }

  /** The tab was hidden or shown. A long-hidden tab pauses; showing it reconnects. */
  setVisible(visible: boolean): void {
    clearTimeout(this.pauseTimer);
    if (!visible) {
      this.pauseTimer = setTimeout(() => {
        this.paused = true;
        clearTimeout(this.retryTimer);
        this.drop();
        this.deps.onStatus("offline");
      }, HIDDEN_PAUSE_MS);
      return;
    }
    if (this.paused) {
      this.paused = false;
      this.reconnectNow();
    }
  }

  /** The browser is back online: do not wait for the backoff. */
  reconnectNow(): void {
    if (this.stopped || this.paused || this.socket !== null) return;
    clearTimeout(this.retryTimer);
    this.attempt = 0;
    void this.connect();
  }

  /** Stopped (signed out) or paused (a long-hidden tab): no socket, no retries. */
  private isResting(): boolean {
    return this.stopped || this.paused;
  }

  private async connect(): Promise<void> {
    if (this.isResting()) return;
    this.deps.onStatus("connecting");
    let ticket: Ticket;
    try {
      ticket = await this.deps.fetchTicket();
    } catch {
      this.scheduleRetry();
      return;
    }
    if (this.isResting()) return;
    const socket = this.deps.openSocket(this.deps.socketUrl(ticket.path, ticket.ticket));
    this.socket = socket;
    this.lastHeard = Date.now();
    socket.onmessage = (frame: MessageEvent) => {
      this.onFrame(socket, frame.data);
    };
    socket.onclose = () => {
      this.onClosed(socket);
    };
  }

  private onFrame(socket: WebSocket, raw: unknown): void {
    if (socket !== this.socket) return;
    this.lastHeard = Date.now();
    const message = parseServerMessage(raw);
    if (message === null) return;
    switch (message.type) {
      case "ready":
        this.attempt = 0;
        this.deps.onStatus("live");
        if (this.everReady) this.deps.onResync();
        this.everReady = true;
        break;
      case "ping":
        socket.send(JSON.stringify({ type: "pong" }));
        break;
      case "resync":
        this.deps.onResync();
        break;
      case "event":
        this.deps.onEvent(message.event);
        break;
    }
  }

  private onClosed(socket: WebSocket): void {
    if (socket !== this.socket) return;
    this.socket = null;
    if (this.isResting()) return;
    // Every close is retried with backoff, whatever its code (backend realtime/connection.py):
    // 4401 means a new ticket, which the next attempt asks for anyway.
    this.scheduleRetry();
  }

  private scheduleRetry(): void {
    if (this.isResting()) return;
    this.deps.onStatus("offline");
    clearTimeout(this.retryTimer);
    const delay = reconnectDelay(this.attempt, this.deps.random);
    this.attempt += 1;
    this.retryTimer = setTimeout(() => {
      void this.connect();
    }, delay);
  }

  private checkSilence(): void {
    if (this.socket === null || Date.now() - this.lastHeard <= SILENCE_LIMIT_MS) return;
    const socket = this.socket;
    this.socket = null;
    socket.close(CLOSE_NORMAL);
    this.scheduleRetry();
  }

  private drop(): void {
    const socket = this.socket;
    this.socket = null;
    if (socket === null) return;
    socket.onmessage = null;
    socket.onclose = null;
    // Closing a socket that is still opening makes the browser log a warning; let it open first.
    if (socket.readyState === WebSocket.CONNECTING) {
      socket.onopen = () => {
        socket.close(CLOSE_NORMAL);
      };
      return;
    }
    socket.close(CLOSE_NORMAL);
  }
}
